import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { PoButtonGroupItem, PoMultiselectOption, PoTableAction, PoTableColumn } from '@po-ui/ng-components';

import { LANGUAGE_OPTIONS } from '../../../admin/curation/curation-constants';
import {
  ReviewStatsHotspot, ReviewStatsHotspotMode, ReviewStatsPeriodFilters
} from '../../interfaces/review-stats.interface';
import { ReviewStatsService } from '../../review-stats.service';
import { CATEGORY_OPTIONS, categoryTags, describeCategories } from '../../utils/review-category-label.util';


interface SourceTag {
  label: string;
  type: 'danger' | 'warning' | 'info' | 'neutral';
}

/** Um trecho da barra de proporção: a severidade e quanto ela é do total. */
interface SeveritySegment {
  severity: 'high' | 'medium' | 'low';
  percent: number;
  title: string;
}

interface SourceQualityRow {
  /**
   * Célula "Fonte": o nome em evidência e, embaixo, a linguagem e a pasta encurtada —
   * as duas últimas pastas, com o caminho inteiro no title.
   */
  sourceCell: { name: string; detail: string; path: string };
  /**
   * Célula de contagem: severidades em tags e a barra com a proporção de cada uma
   * (modo severidade), ou as categorias principais em tags (modo categoria).
   */
  countCell: { tags: SourceTag[]; segments: SeveritySegment[]; title?: string };
  /** Soma das tags da linha: correções no modo severidade, melhorias no de categoria. */
  total: number;
  path: string;
  language: string;
  /** Linguagem como o backend a devolveu — é ela que identifica o fonte na lista de achados. */
  rawLanguage: string;
  high?: number;
  medium?: number;
  low?: number;
  findings: number;
  reviews: number;
  mainCategories?: string;
}

/** Ranking dos fontes que mais exigem atenção no recorte selecionado. */
@Component({
  selector: 'app-review-stats-hotspots',
  templateUrl: './review-stats-hotspots.component.html',
  styleUrls: ['./review-stats-hotspots.component.css']
})
export class ReviewStatsHotspotsComponent implements OnChanges {
  @Input() filters: ReviewStatsPeriodFilters = {};
  /** Altura da grade; sem ela a grade cresce e quem rola é o modal que a hospeda. */
  @Input() height?: number;
  /**
   * Pede os achados de um fonte a quem hospeda a grade. O modal de achados não mora aqui:
   * a grade roda dentro de um modal, e um modal aberto de dentro de outro herda o
   * posicionamento dele — quem hospeda põe os dois lado a lado.
   */
  @Output() findingsRequested = new EventEmitter<{ path: string; language: string }>();

  /** Do número à correção: o que os reviews recentes apontaram neste fonte. */
  readonly actions: PoTableAction[] = [
    { label: 'Ver achados', icon: 'po-icon po-icon-eye', action: (row: SourceQualityRow) => this.showFindings(row) }
  ];

  mode: ReviewStatsHotspotMode = 'severity';
  search = '';
  /** Categoria e linguagem são filtros dedicados — não entram mais na busca livre. */
  selectedCategories: string[] = [];
  selectedLanguages: string[] = [];
  page = 1;
  total = 0;
  loading = false;
  loadError = false;
  readonly pageSize = 20;
  readonly searchLiterals = {
    search: 'Pesquisar por caminho ou arquivo',
    clean: 'Limpar pesquisa'
  };
  readonly categoryOptions: PoMultiselectOption[] = CATEGORY_OPTIONS;
  readonly languageOptions: PoMultiselectOption[] = LANGUAGE_OPTIONS;
  readonly modeButtons: PoButtonGroupItem[] = [
    { label: 'Por severidade', selected: true, action: () => this.setMode('severity') },
    { label: 'Por categoria', action: () => this.setMode('category') }
  ];

  /**
   * Poucas colunas ricas: com uma por contagem e o caminho inteiro numa só, a grade não
   * cabia nem no modal largo. O nome do arquivo vem em evidência, a pasta embaixo, e as
   * contagens em tags.
   */
  /**
   * Fonte e Correções sem largura própria deixavam a tabela crescer com o conteúdo (nome
   * de fonte longo, três tags de severidade) até sobrar espaço nenhum pra coluna de ação —
   * "Ver achados" ficava sempre fora da área visível do modal, mesmo no maior tamanho
   * (`p-size="xl"`). Com as duas em porcentagem e Total/Reviews mais enxutas, a ação
   * sempre tem onde caber.
   */
  readonly severityColumns: PoTableColumn[] = [
    { property: 'sourceCell', label: 'Fonte', type: 'columnTemplate', width: '32%' },
    { property: 'countCell', label: 'Correções', type: 'columnTemplate', width: '32%' },
    { property: 'total', label: 'Total', type: 'number', width: '70px' },
    { property: 'reviews', label: 'Reviews', type: 'number', width: '80px' }
  ];

  readonly categoryColumns: PoTableColumn[] = [
    { property: 'sourceCell', label: 'Fonte', type: 'columnTemplate', width: '32%' },
    { property: 'countCell', label: 'Categorias predominantes', type: 'columnTemplate', width: '32%' },
    { property: 'total', label: 'Total', type: 'number', width: '70px' },
    { property: 'reviews', label: 'Reviews', type: 'number', width: '80px' }
  ];

  rows: SourceQualityRow[] = [];
  private requestSequence = 0;

  constructor(private statsService: ReviewStatsService) {}

  ngOnChanges(): void {
    this.load(true);
  }

  setMode(mode: ReviewStatsHotspotMode): void {
    this.mode = mode;
    this.modeButtons.forEach((button, index) => button.selected = index === (mode === 'severity' ? 0 : 1));
    this.load(true);
  }

  get columns(): PoTableColumn[] {
    return this.mode === 'severity' ? this.severityColumns : this.categoryColumns;
  }

  get hasData(): boolean {
    return this.rows.length > 0;
  }

  get contextLabel(): string {
    const count = this.total.toLocaleString('pt-BR');
    return this.mode === 'severity'
      ? `${count} fontes, priorizados pela gravidade das correções encontradas.`
      : `${count} fontes, priorizados pelo volume de melhorias sugeridas.`;
  }

  get allRowsVisible(): boolean {
    return this.rows.length >= this.total;
  }

  searchRanking(value: string): void {
    this.search = value.trim();
    this.load(true);
  }

  categoriesChanged(values: string[]): void {
    this.selectedCategories = values;
    this.load(true);
  }

  languagesChanged(values: string[]): void {
    this.selectedLanguages = values;
    this.load(true);
  }

  showFindings(row: SourceQualityRow): void {
    this.findingsRequested.emit({ path: row.path, language: row.rawLanguage });
  }

  showMore(): void {
    if (this.loading || this.allRowsVisible) { return; }
    this.page++;
    this.load(false);
  }

  get columnHelp(): string {
    const common =
      '• Fonte: nome do arquivo e, embaixo, a linguagem e as últimas pastas; o caminho completo aparece ao passar o mouse.\n' +
      '• Ver achados: o que os reviews mais recentes apontaram no fonte, com o atalho para cada review.\n' +
      '• Pesquisa, categoria e linguagem se combinam: cada filtro escolhido reduz ainda mais a lista.\n';
    if (this.mode === 'severity') {
      return common +
        '• Correções: quantidade de cada gravidade (Alta, Média, Baixa) e, embaixo, a proporção entre elas.\n' +
        '• Total: soma das três gravidades.\n' +
        '• Reviews: reviews distintos que encontraram ao menos uma correção nesse fonte.';
    }
    return common +
      '• Categorias predominantes: as principais categorias com mais sugestões, com uma tag "+N" quando ' +
      'houver mais — ou, com o filtro de Categoria ativo, todas as categorias do fonte. O número entre ' +
      'parênteses é a quantidade; passe o mouse na célula para ver todas.\n' +
      '• Total: melhorias sugeridas para o fonte no período.\n' +
      '• Reviews: reviews distintos que encontraram ao menos uma sugestão nesse fonte.';
  }

  private load(reset: boolean): void {
    if (reset) {
      this.page = 1;
      this.rows = [];
    }
    this.loading = true;
    this.loadError = false;
    const request = ++this.requestSequence;
    this.statsService.getHotspots({
      ...this.filters, mode: this.mode, search: this.search,
      category: this.selectedCategories, language: this.selectedLanguages,
      page: this.page, pageSize: this.pageSize
    }).subscribe({
      next: result => {
        if (request !== this.requestSequence) { return; }
        const incoming = result.items.map(item =>
          this.mode === 'severity' ? this.severityRow(item) : this.categoryRow(item));
        this.rows = reset ? incoming : [...this.rows, ...incoming];
        this.total = result.total;
        this.loading = false;
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        if (!reset) { this.page--; }
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  /**
   * O caminho partido: o nome em evidência e, como detalhe, a linguagem e as duas
   * últimas pastas. O caminho inteiro quebrava em duas linhas e empurrava a linguagem
   * para o fim; ele continua no title, para quem precisar dele.
   */
  private sourceCell(item: ReviewStatsHotspot): { name: string; detail: string; path: string } {
    const parts = item.path.split(/[\\/]/);
    const name = parts.pop() || item.path;
    const folders = parts.filter(Boolean);
    const shortFolder = folders.length > 2 ? `…/${folders.slice(-2).join('/')}/` : (folders.length ? `${folders.join('/')}/` : '');
    return {
      name,
      detail: [item.language || 'Não classificada', shortFolder].filter(Boolean).join(' · '),
      path: item.path
    };
  }

  /** Proporção de cada severidade, para a barra; severidade zerada não entra. */
  private segments(item: ReviewStatsHotspot, total: number): SeveritySegment[] {
    if (!total) { return []; }
    const parts: [SeveritySegment['severity'], number, string][] = [
      ['high', item.severityHigh, 'Alta'], ['medium', item.severityMedium, 'Média'], ['low', item.severityLow, 'Baixa']
    ];
    return parts
      .filter(([, count]) => count > 0)
      .map(([severity, count, label]) => {
        const percent = Math.round((count / total) * 1000) / 10;
        return { severity, percent, title: `${label}: ${count} (${percent.toLocaleString('pt-BR')}%)` };
      });
  }

  private severityRow(item: ReviewStatsHotspot): SourceQualityRow {
    const findings = item.severityHigh + item.severityMedium + item.severityLow;
    const tags: SourceTag[] = [];
    if (item.severityHigh) { tags.push({ label: `${item.severityHigh} Alta`, type: 'danger' }); }
    if (item.severityMedium) { tags.push({ label: `${item.severityMedium} Média`, type: 'warning' }); }
    if (item.severityLow) { tags.push({ label: `${item.severityLow} Baixa`, type: 'info' }); }
    return {
      sourceCell: this.sourceCell(item),
      countCell: { tags, segments: this.segments(item, findings) },
      total: findings,
      path: item.path,
      language: item.language || 'Não classificada',
      rawLanguage: item.language,
      high: item.severityHigh,
      medium: item.severityMedium,
      low: item.severityLow,
      findings: item.severityHigh + item.severityMedium + item.severityLow,
      reviews: item.severityReviews
    };
  }

  private categoryRow(item: ReviewStatsHotspot): SourceQualityRow {
    // Sem filtro: top 3, com uma tag "+N" quando houver mais (a lista completa fica no
    // title da célula). Com o filtro de categoria ativo, categoryTags devolve todas — a
    // lista já é mais curta, e completude importa mais que compactação: sem isso, filtrar
    // por "Estilo" podia devolver um fonte cuja tag visível não mostrava Estilo nenhum.
    const categories = categoryTags(item.categories, 3, this.selectedCategories);
    const tags: SourceTag[] = categories.tags.map(tag => ({ label: tag.label, type: 'neutral' as const }));
    if (categories.more) {
      tags.push({ label: `+${categories.more}`, type: 'neutral' });
    }
    return {
      sourceCell: this.sourceCell(item),
      countCell: {
        tags,
        segments: [],
        title: describeCategories(item.categories)
      },
      total: item.suggestions,
      path: item.path,
      language: item.language || 'Não classificada',
      rawLanguage: item.language,
      mainCategories: describeCategories(item.categories, 3) || 'Sem categoria',
      findings: item.suggestions,
      reviews: item.categoryReviews
    };
  }
}
